import React, { FC } from 'react';
import {
  DialogContentText,
  Box,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DriveEta } from '@mui/icons-material';
import { FiUser, FiChevronRight } from 'react-icons/fi';

import { Booking } from '../../../logic/api';

import { customerLookUpStyles, ICustomerLookUpResult } from './hooks';

import { useDate } from '../../../utility/useDate';
import { useTranslation } from 'react-i18next';

type SelectItemCallback = (item: ICustomerLookUpResult) => void;

interface IResultsListProps {
  bookings: Booking[];
  onItemSelected: SelectItemCallback;
}

export const ResultsList: FC<IResultsListProps> = ({
  bookings,
  onItemSelected,
}) => (
  <Box>
    <ResultsMessage count={bookings.length} />
    <Box>
      {
        bookings.map((b) => (
          <ResultItem
            key={b.id}
            booking={b}
            onItemSelected={onItemSelected}
          />
        ))
      }
    </Box>
  </Box>
);

interface IResultItemProps {
  booking: Booking | null;
  onItemSelected: SelectItemCallback;
}

export const ResultItem: FC<IResultItemProps> = ({
  booking,
  onItemSelected,
}) => {
  const { toFormattedDateTimeString } = useDate();

  if (!booking) {
    return null;
  }

  const checkOutDate = new Date(booking.vehicle.checkOutDate);
  const lastSeenDate = toFormattedDateTimeString(checkOutDate);

  const { t } = useTranslation();

  const result: ICustomerLookUpResult = {
    leadContact: {
      accountNumber: booking.leadContact.accountNumber,
      email: booking.leadContact.email,
      firstName: booking.leadContact.firstName,
      lastName: booking.leadContact.lastName,
      phoneMobile: booking.leadContact.phoneMobile,
      title: booking.leadContact.title,
    },
    vehicle: {
      colour: booking.vehicle.colour,
      make: booking.vehicle.make,
      model: booking.vehicle.model,
      registration: booking.vehicle.registration,
    },
    vip: booking.vip,
    assistance: booking.assistance,
    paxCount: booking.paxCount,
  };

  return (
    <Card
      variant="outlined"
      sx={customerLookUpStyles.card}
      onClick={() => onItemSelected(result)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={customerLookUpStyles.cardColumn}>
            <Box>
              <FiUser style={customerLookUpStyles.icon} />
            </Box>
            <Box>
              <Typography variant="subtitle1">
                <b>{booking.leadContact.fullName}</b>
              </Typography>
              <Typography variant="subtitle2">{booking.leadContact.email}</Typography>
              <Typography variant="caption">{t("dialogs.lastSeen", { date: lastSeenDate })}</Typography>
            </Box>
          </Box>
          <Box sx={customerLookUpStyles.cardColumn}>
            <Box>
              <DriveEta sx={customerLookUpStyles.icon} />
            </Box>
            <Box>
              <Typography variant="subtitle1">
                <b>{booking.vehicle.registration}</b>
              </Typography>
              <Typography variant="subtitle2">
                {booking.vehicle.make} {booking.vehicle.model}
              </Typography>
              <Typography variant="caption">{booking.vehicle.colour}</Typography>
            </Box>
          </Box>
          <Box>
            <FiChevronRight style={customerLookUpStyles.icon} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

interface IResultsMessageProps {
  count: number;
}

export const ResultsMessage: FC<IResultsMessageProps> = ({ count }) => {
  const { t } = useTranslation();
  if (!count) {
    return <DialogContentText sx={{
      marginBottom: "12px",
    }}>{t("input.validations.noResults")}</DialogContentText>
  }

  return <DialogContentText>{t("dialogs.resultCount", { count: count })}</DialogContentText>
}
